/**
 * FieldScoutDesktopPage — route entry point for the desktop paper-transcription
 * recording mode.
 *
 * Phases:
 *   'selection'  → orchard + scouter selection form (testids: desk-select-orchard, desk-input-scouter, desk-btn-start)
 *   'capture'    → <FieldScoutDesktopCapture> (full dual-mode recorder)
 *
 * Platform gate: if isCompactDevice() render a brief notice linking to the
 * handheld field recorder (/scouting/field-v2).
 *
 * Track: field_scout_recording_desktop / Slice 3 / Task 10.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db, type Orchard, type Pest } from '../../../../db';
import { isCompactDevice } from '../../../../utils/platform';
import { useAuth } from '../../../../contexts/AuthContext';
import { FieldScoutDesktopCapture } from './FieldScoutDesktopCapture';
import './FieldScoutDesktop.css';

// ─────────────────────────────────────────────────────────────────────────────
//  Types
// ─────────────────────────────────────────────────────────────────────────────

type Phase = 'selection' | 'capture';

// ─────────────────────────────────────────────────────────────────────────────
//  Handheld notice — shown when isCompactDevice() is true
// ─────────────────────────────────────────────────────────────────────────────

const HandheldNotice: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="fsd-handheld-notice" data-testid="fsd-handheld-notice">
      <p>{t('fieldScout.desktop.handheldNotice')}</p>
      <Link to="/scouting/field-v2" data-testid="link-field-v2">
        {t('fieldScout.desktop.openManualCapture')}
      </Link>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
//  Page root
// ─────────────────────────────────────────────────────────────────────────────

const FieldScoutDesktopPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  // Platform gate — evaluated once per mount
  const isHandheld = isCompactDevice();

  // Phase state
  const [phase, setPhase] = useState<Phase>('selection');

  // Captured selection values
  const [orchardId, setOrchardId] = useState('');
  const [orchardName, setOrchardName] = useState('');
  const [scouterName, setScouterName] = useState(user?.name ?? '');
  const [pests, setPests] = useState<Pest[]>([]);

  // Orchard + pest lists loaded from Dexie
  const [orchards, setOrchards] = useState<Orchard[]>([]);
  const [allPests, setAllPests] = useState<Pest[]>([]);
  const [listsLoaded, setListsLoaded] = useState(false);

  // Validation error
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([db.orchards.toArray(), db.pests.toArray()]).then(([os, ps]) => {
      setOrchards(
        [...os].sort((a, b) =>
          a.orchardName.localeCompare(b.orchardName, undefined, { numeric: true, sensitivity: 'base' }),
        ),
      );
      setAllPests([...ps].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name)));
      setListsLoaded(true);
    });
  }, []);

  // Handheld redirect notice
  if (isHandheld) {
    return <HandheldNotice />;
  }

  // ── Selection phase ────────────────────────────────────────────────────────

  const handleOrchardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setOrchardId(id);
    setError('');
    // Cache orchardName immediately so handleStart doesn't depend on orchards state timing
    const found = orchards.find((o) => o.id === id);
    if (found) setOrchardName(found.orchardName);
  };

  const handleStart = () => {
    if (!orchardId) {
      setError(t('fieldScout.recording.selectOrchard'));
      return;
    }
    if (!scouterName.trim()) {
      setError(t('fieldScout.recording.scouterName'));
      return;
    }

    const selectedPests = allPests.filter((p) => p.code && p.name);
    setPests(selectedPests);
    setPhase('capture');
  };

  const resetToSelection = () => {
    setPhase('selection');
    setOrchardId('');
    setOrchardName('');
    setScouterName(user?.name ?? '');
    setPests([]);
    setError('');
  };

  if (phase === 'selection') {
    // Show a minimal spinner until orchards + pests have loaded from Dexie.
    // This ensures findByTestId('desk-select-orchard') only resolves after data
    // is ready, so orchardName lookup and pest filtering both work correctly.
    if (!listsLoaded) {
      return (
        <div className="fsd-selection" data-testid="fsd-selection">
          <div className="fsd-loading">{t('fieldScout.recording.start')}…</div>
        </div>
      );
    }

    return (
      <div className="fsd-selection" data-testid="fsd-selection">
        <div className="fsd-selection__card">
          <h2 className="fsd-selection__title">
            {t('fieldScout.desktop.start')}
          </h2>

          {error && (
            <div className="fsd-selection__error" role="alert">{error}</div>
          )}

          <label className="fsd-selection__label">
            {t('fieldScout.desktop.selectOrchard')}
            <select
              className="fsd-selection__input"
              value={orchardId}
              onChange={handleOrchardChange}
              data-testid="desk-select-orchard"
            >
              <option value="">—</option>
              {orchards.map((o) => (
                <option key={o.id} value={o.id}>{o.orchardName}</option>
              ))}
            </select>
          </label>

          <label className="fsd-selection__label">
            {t('fieldScout.recording.scouterName')}
            <input
              type="text"
              className="fsd-selection__input"
              value={scouterName}
              onChange={(e) => { setScouterName(e.target.value); setError(''); }}
              data-testid="desk-input-scouter"
            />
          </label>

          <button
            type="button"
            className="fsd-selection__btn"
            onClick={handleStart}
            data-testid="desk-btn-start"
          >
            {t('fieldScout.desktop.start')}
          </button>
        </div>
      </div>
    );
  }

  // phase === 'capture'
  return (
    <FieldScoutDesktopCapture
      orchardId={orchardId}
      orchardName={orchardName}
      scouterName={scouterName}
      pests={pests}
      onSubmitDone={resetToSelection}
    />
  );
};

export default FieldScoutDesktopPage;
